import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 24px;
  padding: 32px;
  max-width: 400px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Icon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0 0 24px;
  line-height: 1.5;
`;

const Question = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
`;

const MathQuestion = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #1a1f2e;
  margin-bottom: 16px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 24px;
  font-weight: 600;
  text-align: center;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #2DBCAF;
  }
  
  &::placeholder {
    color: #cbd5e1;
    font-size: 16px;
  }
`;

const ErrorMsg = styled.div`
  color: #ef4444;
  font-size: 13px;
  margin-top: 8px;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const Button = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 14px 20px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  ${p => p.$primary ? `
    background: #2DBCAF;
    color: #fff;
    border: none;
    
    &:hover {
      background: #26a89d;
    }
    
    &:disabled {
      background: #94a3b8;
      cursor: not-allowed;
    }
  ` : `
    background: #fff;
    color: #64748b;
    border: 1px solid #e2e8f0;
    
    &:hover {
      background: #f8fafc;
    }
  `}
`;

interface Props {
  onSuccess: () => void;
  onCancel: () => void;
  title?: string;
  subtitle?: string;
}

const generateQuestion = () => {
  const a = Math.floor(Math.random() * 15) + 5; // 5-19
  const b = Math.floor(Math.random() * 15) + 5; // 5-19
  return { a, b, answer: a + b };
};

const ParentalGate: React.FC<Props> = ({ 
  onSuccess, 
  onCancel,
  title = "Verifikasi Orang Tua",
  subtitle = "Untuk melanjutkan, silakan jawab pertanyaan berikut"
}) => {
  const [question, setQuestion] = useState(generateQuestion());
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    // Generate new question on mount
    setQuestion(generateQuestion());
  }, []);

  const handleSubmit = () => {
    const userAnswer = parseInt(input, 10);
    
    if (isNaN(userAnswer)) {
      setError('Masukkan angka');
      return;
    }
    
    if (userAnswer === question.answer) {
      onSuccess();
    } else {
      setAttempts(prev => prev + 1);
      setError('Jawaban salah, coba lagi');
      setInput('');
      
      // Generate new question after 3 failed attempts
      if (attempts >= 2) {
        setQuestion(generateQuestion());
        setAttempts(0);
        setError('Soal baru telah dibuat');
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Overlay onClick={onCancel}>
      <Modal onClick={e => e.stopPropagation()}>
        <Icon>👨‍👩‍👧</Icon>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
        
        <Question>
          <MathQuestion>{question.a} + {question.b} = ?</MathQuestion>
          <Input
            type="number"
            inputMode="numeric"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setError('');
            }}
            onKeyDown={handleKeyDown}
            placeholder="Jawaban"
            autoFocus
          />
          {error && <ErrorMsg>{error}</ErrorMsg>}
        </Question>
        
        <Actions>
          <Button onClick={onCancel}>Batal</Button>
          <Button $primary onClick={handleSubmit} disabled={!input}>
            Lanjutkan
          </Button>
        </Actions>
      </Modal>
    </Overlay>
  );
};

export default ParentalGate;
